import React, { useEffect } from 'react'
import { useLocation } from 'react-router'

import { siteHasIntlLocation } from 'src/lib/js/hooks/useIsIntlRestaurant'
import { useSiteHasUsAdsLocation } from 'src/lib/js/hooks/useIsAdsRestaurant'
import useScrollToTopOnNewLocation from 'src/lib/js/hooks/useScrollToTopOnNewLocation'
import TagManager from 'src/lib/js/react-gtm/TagManagerScript'
import { getHost } from 'src/lib/js/utilities'
import { StructuredDataProvider } from 'src/public/components/seo/context'
import tracker from 'src/lib/js/tracker'
import ToastProduct from 'src/public/components/online_ordering/ToastProduct'
import { RequestContext } from 'src/lib/js/context'

import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import { SiteMeta } from 'public/components/default_template/meta/Meta'
import { SiftMeta } from 'public/components/default_template/meta/sift/SiftMeta'
import Popups from 'public/components/default_template/popups/Popups'
import { TrackingIFrame } from './tracking/TrackingIFrame'

import { getPrimaryCustomDomain } from 'src/shared/js/utils/DomainUtils'
import { useSSRCookieConsentContext } from 'src/server/SSRCookieConsentContext'
import { useCookieConsentContext } from 'src/public/js/cookie_consent/CookieConsentContext'
import { useFlags } from 'src/shared/components/common/feature_flags/useFlags'
import { isToastLocalRequest } from 'src/public/js/siteUtilities'
import { useStaticSiteContext } from '../../app/StaticSiteContext'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'

type Props = {
  titleTag?: string
  descriptionTag?: string
  staticContext?: RequestContext
  noPopups?: boolean
}

/**
 * Shared PageShell infrastructure component providing:
 * - Scroll to top on navigation
 * - Guest Analytics initialization and page view tracking
 * - Ethyca cookie consent setup
 * - Meta tags (SiteMeta, SiftMeta)
 * - GTM (TagManager) and tracking iframe
 * - StructuredDataProvider
 * - Popups
 *
 * Used by both PageShell and EcommercePageShell to avoid duplication.
 */
export const PageShellInfrastructure = ({
  titleTag,
  descriptionTag,
  staticContext,
  noPopups = false,
  children
}: React.PropsWithChildren<Props>) => {
  useScrollToTopOnNewLocation()
  const location = useLocation()

  const {
    restaurant,
    restaurant: { meta, pixels, pixelsV2, content, theme },
    selectedLocation: { meta: locationMeta },
    toastProduct
  } = useRestaurant()

  const { ooEthycaAds } = useFlags()

  const { analyticsConsent, marketingConsent, functionalConsent } =
    useCookieConsentContext()

  // GTM may contain unknown third-party tags, so the tracking iframe
  // (which runs non-Google tags) is only enabled when all consent
  // categories are granted.
  const hasFullConsent =
    analyticsConsent && marketingConsent && functionalConsent

  // Initialize Guest Analytics through tracker once per session
  // Only enable for sites other than OO Essentials/Basic
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      restaurant?.id &&
      toastProduct !== ToastProduct.OOBasic
    ) {
      tracker.initGuestAnalytics(restaurant.id)
    }
  }, [restaurant?.id, toastProduct])

  // Track page views through tracker on all route changes
  useEffect(() => {
    if (typeof window === 'undefined' || !restaurant?.id) {
      return
    }

    const timer = setTimeout(() => {
      tracker.trackGuestAnalytics(TrackingEvents.PAGE_VIEW)
    }, 0)
    return () => clearTimeout(timer)
  }, [location.pathname, restaurant?.id])

  // Set whether Ethyca is enabled based on the current page
  const hasIntlLocation = siteHasIntlLocation(restaurant)
  const hasUsAdsLocation = useSiteHasUsAdsLocation()
  const ssrCookieConsentContext = useSSRCookieConsentContext()
  const staticContextFromHook = useStaticSiteContext()

  if (ssrCookieConsentContext) {
    const isAdsEnabled = (ooEthycaAds ?? false) && hasUsAdsLocation
    const isEthycaEnabled = !isToastLocalRequest(staticContextFromHook)
    ssrCookieConsentContext.setEthycaEnabled(isEthycaEnabled)
    ssrCookieConsentContext.setIsAdsEnabled(isAdsEnabled)
  }

  return (
    <StructuredDataProvider>
      <SiftMeta />
      {/* The Cookie Consent banner doesn't know about cookies that GTM might add,
          so we can't use GTM for Int'l websites currently. */}
      {!hasIntlLocation && pixelsV2.google_tag_manager?.scriptArg ? (
        <>
          <TagManager gtmId={pixelsV2.google_tag_manager.scriptArg} />
          {hasFullConsent && (
            <TrackingIFrame
              googleTagManagerId={pixelsV2.google_tag_manager.scriptArg}
            />
          )}
        </>
      ) : null}
      <SiteMeta
        {...meta}
        {...locationMeta}
        legacyTheme={meta.legacyTheme}
        siteTheme={theme}
        primaryColor={meta.primaryColor || locationMeta.primaryColor}
        titleTag={titleTag}
        description={descriptionTag}
        primaryCustomDomain={getPrimaryCustomDomain(restaurant)}
        domain={getHost(staticContext)}
        pixels={pixels}
        pixelsV2={pixelsV2}
        externalModules={content?.externalModules}
      />
      {!noPopups ? <Popups /> : null}
      {children}
    </StructuredDataProvider>
  )
}
